import {
  CheckCircle2,
  Clock,
  GripVertical,
  Loader2,
  Pencil,
  Plus,
  Settings2,
  Tag,
  Target,
  Trash2,
  X,
} from "lucide-react";
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import Swal from "sweetalert2";
import { Modal } from "../components";
import { useActiveContext } from "../contexts/ActiveContextContext";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "../contexts/LanguageContext";
import { cn } from "../utils/cn";
import Skeleton from "../components/ui/Skeleton";
import { showToastError, showToastSuccess } from "../shared/alertService";
import apiService from "../shared/apiService";
import {
  getPlatformConfig,
  getPlatformName,
  PlatformIcon,
} from "../utils/platformIcons";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface LeadTag {
  id: number;
  name: string;
  color: string;
  meta_audience_id?: string | null;
}

interface LeadStage {
  // Funil do Virtus Chat: id e key são o id da etapa lá.
  id: number | string;
  key: string;
  label: string;
  color: string;
  order: number;
  is_system: boolean;
}

interface KanbanLead {
  id: number;
  name?: string;
  email: string;
  phone?: string;
  platform: string;
  status: string;
  lead_score: number;
  qualification_status?: string;
  campaign?: { name: string } | null;
  tags?: number[];
  created_at: string;
  lead_source_system?: string;
}

interface KanbanColumn {
  id: number | string;
  key: string;
  label: string;
  color: string;
  order: number;
  is_system: boolean;
  leads: KanbanLead[];
  count: number;
}

/** Funil do Virtus Chat (o Chat é o dono; o LeadFlow faz a ponte). */
interface ChatPipeline {
  id: string;
  name: string;
  stages: { id: string; name: string; color?: string }[];
}

// Colunas do Kanban de um funil do Chat que não são etapas dele.
const NO_DEAL_COLUMN = "none";
const DEAL_RESULT_COLUMNS = ["won", "lost"];

export interface LeadKanbanProps {
  availableTags: LeadTag[];
  onTagsChange?: () => void;
  filters?: Record<string, any>;
  /** Se o usuário pode mexer nas etapas do quadro mostrado (funil do Chat: só administradores). */
  onCanEditStagesChange?: (canEdit: boolean) => void;
}

export interface LeadKanbanRef {
  openStageManager: () => void;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TagSelector({
  lead,
  availableTags,
  onUpdate,
}: {
  lead: KanbanLead;
  availableTags: LeadTag[];
  onUpdate: (leadId: number, tags: number[]) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const toggle = async (tagId: number) => {
    const current = lead.tags || [];
    const next = current.includes(tagId)
      ? current.filter((tg) => tg !== tagId)
      : [...current, tagId];
    setSaving(true);
    try {
      await apiService.patch(`/api/ads/leads/${lead.id}/tags`, { tags: next });
      onUpdate(lead.id, next);
    } catch {
      showToastError(t("Erro ao atualizar tags"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          setOpen((p) => !p);
        }}
        className="p-1 rounded-md hover:bg-black/10 transition-colors"
        title={t("Gerenciar tags")}
      >
        {saving ? (
          <Loader2 className="w-3 h-3 animate-spin text-gray-400" />
        ) : (
          <Tag className="w-3 h-3 text-gray-400 hover:text-gray-600" />
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-purple-200 rounded-xl shadow-2xl p-2 min-w-[160px]">
          <div className="text-[8px] font-black uppercase tracking-widest text-gray-400 px-2 pb-1.5 border-b border-gray-100 mb-1.5">
            {t("Tags")}
          </div>
          {availableTags.length === 0 && (
            <p className="text-[9px] text-gray-400 px-2 py-1">
              {t("Nenhuma tag criada")}
            </p>
          )}
          {availableTags.map((tag) => {
            const selected = (lead.tags || []).includes(tag.id);
            return (
              <button
                key={tag.id}
                onClick={() => toggle(tag.id)}
                className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-[10px] font-bold transition-colors ${selected ? "bg-purple-50" : "hover:bg-gray-50"}`}
              >
                <span
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: tag.color }}
                />
                <span className="flex-1 text-left text-gray-700">
                  {tag.name}
                </span>
                {selected && (
                  <CheckCircle2 className="w-3 h-3 text-purple-600" />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function KanbanCard({
  lead,
  availableTags,
  isDragging,
  onTagUpdate,
}: {
  lead: KanbanLead;
  availableTags: LeadTag[];
  isDragging: boolean;
  onTagUpdate: (leadId: number, tags: number[]) => void;
}) {
  const { t } = useTranslation();
  const tagObjs = availableTags.filter((tag) =>
    (lead.tags || []).includes(tag.id),
  );
  const score = Math.min(100, lead.lead_score);

  return (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData("leadId", String(lead.id))}
      className={cn(
        "bg-white rounded-2xl border border-purple-200 p-4 cursor-grab active:cursor-grabbing select-none",
        "hover:border-purple-300 hover:shadow-lg hover:shadow-purple-500/10 transition-all duration-200",
        isDragging &&
          "opacity-50 scale-95 rotate-1 shadow-2xl shadow-purple-500/20",
      )}
    >
      {/* Header: Platform & Score */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              "w-8 h-8 rounded-xl border flex items-center justify-center shrink-0",
              getPlatformConfig(lead.platform).bgColor,
              getPlatformConfig(lead.platform).color,
              getPlatformConfig(lead.platform).borderColor,
            )}
          >
            <PlatformIcon platform={lead.platform} size={16} />
          </div>
          <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
            {getPlatformName(lead.platform)}
          </span>
        </div>
        <div className="flex items-center gap-1.5 bg-gray-50 px-2 py-1 rounded-lg border border-gray-100">
          <div className="w-10 h-1.5 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${score}%`,
                backgroundColor:
                  score >= 70 ? "#10b981" : score >= 40 ? "#f59e0b" : "#ef4444",
              }}
            />
          </div>
          <span className="text-[9px] font-black text-gray-400">{score}%</span>
        </div>
      </div>

      {/* Lead Info */}
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-linear-to-br from-purple-100 to-indigo-50 flex items-center justify-center text-purple-600 font-black text-[10px] shrink-0 border border-purple-200">
          {(lead.name || lead.email).charAt(0).toUpperCase()}
        </div>
        <div className="min-w-0">
          <p className="text-[12px] font-black text-gray-900 truncate leading-tight mb-0.5">
            {lead.name || t("Sem nome")}
          </p>
          <p className="text-[10px] font-bold text-gray-400 truncate leading-tight">
            {lead.email}
          </p>
        </div>
      </div>

      {/* Footer: Tags, Settings & Date */}
      <div className="pt-3 border-t border-purple-50 flex flex-col gap-2">
        <div className="flex flex-wrap items-center gap-1.5">
          {tagObjs.map((t) => (
            <span
              key={t.id}
              className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full border"
              style={{
                background: `${t.color}20`,
                borderColor: `${t.color}40`,
                color: t.color,
              }}
            >
              {t.name}
            </span>
          ))}
          <TagSelector
            lead={lead}
            availableTags={availableTags}
            onUpdate={onTagUpdate}
          />
        </div>

        <div className="flex items-center justify-between mt-1">
          <span className="text-[9px] text-gray-400 font-medium">
            {new Date(lead.created_at).toLocaleString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
          {lead.lead_source_system === "leadflow" && (
            <span className="text-[9px] font-black uppercase tracking-widest text-indigo-400">
              Leadflow
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main kanban component
// ---------------------------------------------------------------------------

const LeadKanban = forwardRef<LeadKanbanRef, LeadKanbanProps>(
  ({ availableTags, onTagsChange, filters, onCanEditStagesChange }, ref) => {
    const { t } = useTranslation();
    const { activeContext } = useActiveContext();
    const { user } = useAuth();
    const KANBAN_PAGE_SIZE = 30;

    const [columns, setColumns] = useState<KanbanColumn[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingMoreCol, setLoadingMoreCol] = useState<string | null>(null);
    const [dragOverCol, setDragOverCol] = useState<string | null>(null);
    const [stageManagerOpen, setStageManagerOpen] = useState(false);
    // Funis do Virtus Chat da conta; "" = o Kanban do SMM.
    const [pipelines, setPipelines] = useState<ChatPipeline[]>([]);
    const [pipelineId, setPipelineId] = useState("");
    const scrollRef = useRef<HTMLDivElement>(null);
    // Como no Chat, só administradores mudam as etapas de um funil de lá.
    const canEditStages =
      !pipelineId ||
      user?.org_role === "ADMIN" ||
      user?.org_role === "SUPER_ADMIN";

    useEffect(() => {
      onCanEditStagesChange?.(canEditStages);
    }, [canEditStages, onCanEditStagesChange]);

    // Drag-to-scroll states
    const [isDraggingBoard, setIsDraggingBoard] = useState(false);
    const [startX, setStartX] = useState(0);
    const [scrollLeft, setScrollLeft] = useState(0);

    useImperativeHandle(ref, () => ({
      openStageManager: () => {
        if (canEditStages) setStageManagerOpen(true);
      },
    }));

    const filtersJson = JSON.stringify(filters || {});

    // Compartilhado entre a carga inicial (/kanban) e a paginação por coluna
    // (/kanban/column/<status>) — os dois endpoints aceitam os mesmos filtros.
    const buildKanbanParams = useCallback(() => {
      const params = new URLSearchParams();
      if (activeContext?.organization?.id)
        params.append("organization_id", String(activeContext.organization.id));
      if (activeContext?.clientCompany?.id)
        params.append("company_id", String(activeContext.clientCompany.id));

      const parsedFilters = JSON.parse(filtersJson);
      if (parsedFilters) {
        if (parsedFilters.status) params.append("status", parsedFilters.status);
        if (parsedFilters.qualification_status)
          params.append(
            "qualification_status",
            parsedFilters.qualification_status,
          );
        if (parsedFilters.platform)
          params.append("platform", parsedFilters.platform);
        if (parsedFilters.is_duplicate)
          params.append("is_duplicate", String(parsedFilters.is_duplicate));
        if (parsedFilters.is_cancelled)
          params.append("is_cancelled", String(parsedFilters.is_cancelled));
        if (parsedFilters.is_fraud)
          params.append("is_fraud", String(parsedFilters.is_fraud));
        if (parsedFilters.search) params.append("search", parsedFilters.search);
      }
      if (pipelineId) params.append("pipeline_id", pipelineId);
      return params;
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeContext, filtersJson, pipelineId]);

    useEffect(() => {
      // Conta sem Virtus Chat (ou LeadFlow fora): fica só o Kanban do SMM.
      setPipelineId("");
      apiService
        .get("/api/ads/leads/pipelines")
        .then((data) => setPipelines(data?.success ? data.pipelines || [] : []))
        .catch(() => setPipelines([]));
    }, [activeContext]);

    const load = useCallback(async () => {
      setLoading(true);
      try {
        const params = buildKanbanParams();
        const data = await apiService.get(`/api/ads/leads/kanban?${params}`);
        if (data.success) setColumns(data.columns || []);
      } catch (e: any) {
        showToastError(e.message || t("Erro ao carregar Kanban"));
      } finally {
        setLoading(false);
      }
      // `t` (useTranslation) é recriada a cada render do provider — incluí-la
      // aqui reintroduz o mesmo loop infinito que `filtersJson` corrigiu para
      // `filters` (load muda de identidade a cada render → useEffect abaixo
      // dispara de novo → setLoading → novo render → t muda de novo...).
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildKanbanParams]);

    useEffect(() => {
      load();
    }, [load]);

    // Cada coluna é paginada de forma independente — "Carregar mais" busca a
    // próxima página só daquela coluna e concatena no estado local, sem
    // recarregar o board inteiro.
    const loadMoreColumn = async (statusKey: string) => {
      if (loadingMoreCol) return;
      const col = columns.find((c) => c.key === statusKey);
      if (!col) return;

      setLoadingMoreCol(statusKey);
      try {
        const params = buildKanbanParams();
        params.append("offset", String(col.leads.length));
        params.append("limit", String(KANBAN_PAGE_SIZE));
        const data = await apiService.get(
          `/api/ads/leads/kanban/column/${statusKey}?${params}`,
        );
        if (data.success) {
          setColumns((prev) =>
            prev.map((c) =>
              c.key === statusKey
                ? { ...c, leads: [...c.leads, ...(data.leads || [])] }
                : c,
            ),
          );
        }
      } catch (e: any) {
        showToastError(e.message || t("Erro ao carregar mais leads"));
      } finally {
        setLoadingMoreCol(null);
      }
    };

    const handleDrop = async (targetStatus: string, e: React.DragEvent) => {
      e.preventDefault();
      const leadId = Number(e.dataTransfer.getData("leadId"));
      if (!leadId) return;
      // "Sem negócio neste funil" é só de onde se arrasta.
      if (pipelineId && targetStatus === NO_DEAL_COLUMN) return;

      const srcCol = columns.find((c) => c.leads.some((l) => l.id === leadId));
      if (!srcCol || srcCol.key === targetStatus) return;

      setColumns((prev) =>
        prev.map((col) => {
          if (col.key === srcCol.key)
            return {
              ...col,
              leads: col.leads.filter((l) => l.id !== leadId),
              count: col.count - 1,
            };
          if (col.key === targetStatus) {
            const moved = srcCol.leads.find((l) => l.id === leadId)!;
            return {
              ...col,
              leads: [{ ...moved, status: targetStatus }, ...col.leads],
              count: col.count + 1,
            };
          }
          return col;
        }),
      );
      setDragOverCol(null);

      try {
        if (pipelineId) {
          // Perdido num funil do Chat: pede o motivo (opcional), como a tela do Chat.
          let lostReason: string | undefined;
          if (targetStatus === "lost") {
            const answer = await Swal.fire({
              title: t("Motivo da perda"),
              input: "text",
              inputPlaceholder: t("Ex.: sem orçamento (opcional)"),
              inputAttributes: { maxlength: "200" },
              showCancelButton: true,
              confirmButtonText: t("Marcar como perdido"),
              cancelButtonText: t("Cancelar"),
            });
            if (!answer.isConfirmed) {
              load();
              return;
            }
            lostReason = (answer.value || "").trim() || undefined;
          }
          // Funil do Chat: o negócio muda lá (ou é aberto, se a pessoa não tiver um neste funil).
          await apiService.patch(`/api/ads/leads/${leadId}/deal`, {
            pipeline_id: pipelineId,
            column: targetStatus,
            lost_reason: lostReason,
          });
        } else {
          await apiService.patch(`/api/ads/leads/${leadId}/status`, {
            status: targetStatus,
          });
        }
        const label =
          columns.find((c) => c.key === targetStatus)?.label || targetStatus;
        showToastSuccess(t("Lead movido para {label}", { label }));
        onTagsChange?.();
      } catch (e: any) {
        showToastError((pipelineId && e?.message) || t("Erro ao mover lead"));
        load();
      }
    };

    const handleTagUpdate = (leadId: number, tags: number[]) => {
      setColumns((prev) =>
        prev.map((col) => ({
          ...col,
          leads: col.leads.map((l) => (l.id === leadId ? { ...l, tags } : l)),
        })),
      );
      onTagsChange?.();
    };

    if (loading) {
      return (
        <div className="w-full animate-pulse">
          {/* Toolbar Skeleton */}
          <div className="flex items-center justify-between mb-4">
            <Skeleton
              variant="rectangular"
              width={120}
              height={16}
              className="rounded-sm"
            />
            <Skeleton
              variant="rectangular"
              width={140}
              height={28}
              className="rounded-xl"
            />
          </div>

          {/* Board Skeleton */}
          <div className="flex gap-4 overflow-x-auto pb-4 kanban-scroll min-h-[520px]">
            {[1, 2, 3, 4, 5].map((col) => (
              <div
                key={col}
                className="flex flex-col min-w-[240px] flex-1 max-w-[310px]"
              >
                <Skeleton
                  variant="rectangular"
                  width="100%"
                  height={44}
                  className="rounded-2xl mb-3"
                />
                <div className="rounded-2xl border-2 border-dashed border-purple-100 p-2 space-y-2 min-h-[160px]">
                  {[1, 2].map((c) => (
                    <Skeleton
                      key={c}
                      variant="rectangular"
                      width="100%"
                      height={140}
                      className="rounded-2xl"
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <>
        {pipelines.length > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <label
              htmlFor="kanban-pipeline"
              className="text-[10px] font-black uppercase tracking-widest text-gray-400"
            >
              {t("Funil")}
            </label>
            <select
              id="kanban-pipeline"
              value={pipelineId}
              onChange={(e) => setPipelineId(e.target.value)}
              className="h-8 px-2 text-[11px] font-bold rounded-xl border border-purple-200 bg-white focus:outline-hidden focus:ring-2 focus:ring-purple-300"
            >
              <option value="">{t("Kanban do SMM")}</option>
              {pipelines.map((p) => (
                <option key={p.id} value={p.id}>
                  {t("Virtus Chat: {name}", { name: p.name })}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Scrollable board */}
        <div
          ref={scrollRef}
          className={cn(
            "flex gap-4 pb-4 h-[calc(100vh-270px)] min-h-[400px] kanban-scroll transition-colors",
            isDraggingBoard ? "cursor-grabbing select-none" : "cursor-grab",
          )}
          style={{
            overflowX: "auto",
            scrollbarWidth: "thin",
            scrollbarColor: "#a78bfa #f3f4f6",
          }}
          onMouseDown={(e) => {
            const target = e.target as HTMLElement;
            if (
              target.closest("[draggable]") ||
              target.closest("button") ||
              target.closest("input")
            ) {
              return;
            }
            if (!scrollRef.current) return;
            setIsDraggingBoard(true);
            setStartX(e.pageX - scrollRef.current.offsetLeft);
            setScrollLeft(scrollRef.current.scrollLeft);
          }}
          onMouseLeave={() => setIsDraggingBoard(false)}
          onMouseUp={() => setIsDraggingBoard(false)}
          onMouseMove={(e) => {
            if (!isDraggingBoard || !scrollRef.current) return;
            e.preventDefault();
            const x = e.pageX - scrollRef.current.offsetLeft;
            const walk = (x - startX) * 2;
            scrollRef.current.scrollLeft = scrollLeft - walk;
          }}
        >
          {columns.map((col) => (
            <div
              key={col.key}
              className="flex flex-col min-w-[240px] flex-1 max-w-[310px]"
              onDragOver={(e) => {
                e.preventDefault();
                setDragOverCol(col.key);
              }}
              onDragLeave={() => setDragOverCol(null)}
              onDrop={(e) => handleDrop(col.key, e)}
            >
              {/* Column header */}
              <div
                className={cn(
                  "flex items-center justify-between px-4 py-3 rounded-2xl border mb-3 transition-all duration-200",
                  dragOverCol === col.key && "ring-2 shadow-md scale-[1.02]",
                )}
                style={{
                  backgroundColor: col.color + "15",
                  borderColor: col.color + "33",
                  ...(dragOverCol === col.key
                    ? ({ "--tw-ring-color": col.color } as React.CSSProperties)
                    : {}),
                }}
              >
                <div className="flex items-center gap-2">
                  <Target size={14} style={{ color: col.color }} />
                  <span
                    className="text-[11px] font-black uppercase tracking-widest"
                    style={{ color: col.color }}
                  >
                    {col.label}
                  </span>
                </div>
                <span
                  className="text-[10px] font-black w-6 h-6 rounded-full flex items-center justify-center border"
                  style={{
                    background: `${col.color}20`,
                    borderColor: `${col.color}40`,
                    color: col.color,
                  }}
                >
                  {col.count}
                </span>
              </div>

              {/* Drop zone */}
              <div
                className={cn(
                  "flex-1 rounded-2xl border-2 border-dashed p-2 flex flex-col gap-2 transition-all duration-200 overflow-y-auto kanban-scroll",
                )}
                style={{
                  borderColor:
                    dragOverCol === col.key ? col.color : col.color + "40",
                  background:
                    dragOverCol === col.key ? `${col.color}0D` : "transparent",
                  borderStyle: dragOverCol === col.key ? "solid" : "dashed",
                }}
              >
                {col.leads.length === 0 ? (
                  <div className="flex-1 flex flex-col items-center justify-center py-8 text-center gap-2">
                    {dragOverCol === col.key ? (
                      <>
                        <Target size={28} style={{ color: col.color }} />
                        <p
                          className="text-[11px] font-black uppercase tracking-widest"
                          style={{ color: col.color }}
                        >
                          {t("Solte para {label}", {
                            label: col.label.toLowerCase(),
                          })}
                        </p>
                      </>
                    ) : (
                      <>
                        <Clock size={24} className="text-gray-300" />
                        <p className="text-[10px] text-gray-400 font-medium uppercase tracking-widest">
                          {t("Arraste aqui")}
                        </p>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    {col.leads.map((lead) => (
                      <KanbanCard
                        key={lead.id}
                        lead={lead}
                        availableTags={availableTags}
                        isDragging={false}
                        onTagUpdate={handleTagUpdate}
                      />
                    ))}
                    {col.leads.length < col.count && (
                      <button
                        type="button"
                        onClick={() => loadMoreColumn(col.key)}
                        disabled={loadingMoreCol === col.key}
                        className="flex items-center justify-center gap-1.5 py-2 rounded-xl text-[9px] font-black uppercase tracking-widest text-gray-400 hover:text-purple-600 hover:bg-purple-50/50 transition-all disabled:opacity-60"
                      >
                        {loadingMoreCol === col.key ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          t("Carregar mais ({count})", {
                            count: col.count - col.leads.length,
                          })
                        )}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}

          {/* Add column button */}
          {canEditStages && (
            <div className="flex flex-col min-w-[240px] flex-1 max-w-[310px] items-start pt-0">
              <button
                onClick={() => setStageManagerOpen(true)}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border-2 border-dashed border-purple-200 text-purple-400 hover:border-purple-400 hover:text-purple-600 hover:bg-purple-50/50 transition-all text-[10px] font-black uppercase tracking-widest"
              >
                <Plus className="w-4 h-4" />
                {t("Nova Etapa")}
              </button>
            </div>
          )}
        </div>

        {stageManagerOpen && (
          <StageManagerModal
            open={stageManagerOpen}
            onClose={() => setStageManagerOpen(false)}
            onChanged={load}
            pipelineId={pipelineId}
            initialStages={columns
              .filter(
                (c) =>
                  !pipelineId ||
                  ![NO_DEAL_COLUMN, ...DEAL_RESULT_COLUMNS].includes(c.key),
              )
              .map((c) => ({
                id: c.id,
                key: c.key,
                label: c.label,
                color: c.color,
                order: c.order,
                is_system: c.is_system,
              }))}
          />
        )}
      </>
    );
  },
);

LeadKanban.displayName = "LeadKanban";

// ---------------------------------------------------------------------------
// Stage Manager Modal
// ---------------------------------------------------------------------------

const PRESET_COLORS = [
  "#6366f1",
  "#3b82f6",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#ec4899",
  "#06b6d4",
  "#84cc16",
  "#8b5cf6",
  "#f97316",
  "#14b8a6",
  "#64748b",
];

function StageManagerModal({
  open,
  onClose,
  onChanged,
  initialStages,
  pipelineId,
}: {
  open: boolean;
  onClose: () => void;
  onChanged: () => void;
  initialStages: LeadStage[];
  /** Funil do Virtus Chat: as etapas são editadas lá (pelo LeadFlow). */
  pipelineId?: string;
}) {
  const { t } = useTranslation();
  const [stages, setStages] = useState<LeadStage[]>(initialStages);
  const [newLabel, setNewLabel] = useState("");
  const [newColor, setNewColor] = useState("#6366f1");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<number | string | null>(null);
  const [editLabel, setEditLabel] = useState("");
  const [editColor, setEditColor] = useState("");

  // Atualiza quando o kanban recarrega (após CRUD)
  useEffect(() => {
    setStages(initialStages);
  }, [initialStages]);

  // Funil do Chat: manda a lista inteira, na ordem (sem id = etapa nova).
  const saveChatStages = (
    list: { id?: string; name: string; color: string }[],
  ) =>
    apiService.put(`/api/ads/leads/pipelines/${pipelineId}/stages`, {
      stages: list,
    });
  const chatStages = () =>
    stages.map((s) => ({ id: String(s.key), name: s.label, color: s.color }));

  const handleCreate = async () => {
    if (!newLabel.trim()) return;
    setSaving(true);
    try {
      const data = pipelineId
        ? await saveChatStages([
            ...chatStages(),
            { name: newLabel.trim(), color: newColor },
          ])
        : await apiService.post("/api/ads/leads/stages", {
            label: newLabel.trim(),
            color: newColor,
          });
      if (data.success) {
        setNewLabel("");
        showToastSuccess(t("Etapa criada!"));
        onChanged(); // recarrega o kanban, que atualiza initialStages e sincroniza stages via useEffect
      } else {
        showToastError(data.error || t("Erro ao criar etapa"));
      }
    } catch (e: any) {
      showToastError(e.message || t("Erro ao criar etapa"));
    } finally {
      setSaving(false);
    }
  };

  const handleSaveEdit = async (id: number | string) => {
    try {
      if (pipelineId) {
        await saveChatStages(
          chatStages().map((s) =>
            s.id === id ? { ...s, name: editLabel, color: editColor } : s,
          ),
        );
      } else {
        await apiService.patch(`/api/ads/leads/stages/${id}`, {
          label: editLabel,
          color: editColor,
        });
      }
      showToastSuccess(t("Etapa atualizada!"));
      setEditingId(null);
      onChanged();
    } catch {
      showToastError(t("Erro ao salvar etapa"));
    }
  };

  const handleDelete = async (stage: LeadStage) => {
    if (stage.is_system) {
      showToastError(t("Etapas padrão não podem ser excluídas"));
      return;
    }
    if (pipelineId) {
      try {
        await apiService.delete(
          `/api/ads/leads/pipelines/${pipelineId}/stages/${stage.key}`,
        );
        showToastSuccess(t("Etapa removida."));
        onChanged();
      } catch (e: any) {
        // Etapa com negócios: o Chat recusa e pede para movê-los antes.
        showToastError(e?.message || t("Erro ao remover etapa"));
      }
      return;
    }
    try {
      const data = await apiService.delete(`/api/ads/leads/stages/${stage.id}`);
      const msg =
        data.moved_leads > 0
          ? t('Etapa removida. {count} lead(s) movidos para "Novo".', {
              count: data.moved_leads,
            })
          : t("Etapa removida.");
      showToastSuccess(msg);
      onChanged();
    } catch {
      showToastError(t("Erro ao remover etapa"));
    }
  };

  const startEdit = (stage: LeadStage) => {
    setEditingId(stage.id);
    setEditLabel(stage.label);
    setEditColor(stage.color);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Etapas do Kanban")}
      subtitle={t("Personalize as colunas do funil de vendas")}
      icon={<Settings2 className="w-5 h-5" />}
      maxWidth="md"
    >
      <div className="flex flex-col p-6 h-[70vh] sm:h-auto sm:max-h-[80vh]">
        {/* Create new stage */}
        <div className="flex flex-col gap-2 mb-5 shrink-0">
          <div className="flex gap-2">
            <input
              type="text"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              placeholder={t("Nome da nova etapa...")}
              maxLength={50}
              className="flex-1 min-w-0 h-10 px-3 text-[11px] font-bold rounded-xl border border-purple-200 focus:outline-hidden focus:ring-2 focus:ring-purple-300 bg-gray-50"
            />
            <button
              onClick={handleCreate}
              disabled={saving || !newLabel.trim()}
              className="w-10 h-10 bg-purple-600 text-white rounded-xl flex items-center justify-center hover:bg-purple-700 transition-colors disabled:opacity-50 shrink-0"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
            </button>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {PRESET_COLORS.slice(0, 8).map((c) => (
              <button
                key={c}
                onClick={() => setNewColor(c)}
                className={`w-5 h-5 rounded-full transition-transform ${newColor === c ? "scale-125 ring-2 ring-offset-1 ring-gray-400" : ""}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        {/* Stage list */}
        <div className="overflow-y-auto flex-1 space-y-2 pr-1">
          {stages.length === 0 ? (
            <p className="text-center text-[10px] text-gray-400 py-8 font-bold uppercase tracking-widest">
              {t("Nenhuma etapa configurada")}
            </p>
          ) : (
            stages.map((stage) => (
              <div
                key={stage.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-100 hover:border-purple-200 transition-colors"
              >
                <GripVertical className="w-3 h-3 text-gray-300 shrink-0" />

                {editingId === stage.id ? (
                  <>
                    <div className="flex items-center gap-1 shrink-0">
                      {PRESET_COLORS.slice(0, 8).map((c) => (
                        <button
                          key={c}
                          onClick={() => setEditColor(c)}
                          className={`w-4 h-4 rounded-full transition-transform ${editColor === c ? "scale-125 ring-1 ring-offset-1 ring-gray-400" : ""}`}
                          style={{ backgroundColor: c }}
                        />
                      ))}
                    </div>
                    <input
                      autoFocus
                      value={editLabel}
                      onChange={(e) => setEditLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleSaveEdit(stage.id);
                        if (e.key === "Escape") setEditingId(null);
                      }}
                      className="flex-1 h-7 px-2 text-[11px] font-bold rounded-lg border border-purple-300 focus:outline-hidden focus:ring-1 focus:ring-purple-400"
                    />
                    <button
                      onClick={() => handleSaveEdit(stage.id)}
                      className="p-1 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <CheckCircle2 className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => setEditingId(null)}
                      className="p-1 hover:bg-gray-100 rounded-lg transition-colors text-gray-400"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </>
                ) : (
                  <>
                    <div
                      className="w-3 h-3 rounded-full shrink-0"
                      style={{ backgroundColor: stage.color }}
                    />
                    <span className="flex-1 text-[11px] font-bold text-gray-700">
                      {stage.label}
                    </span>
                    {stage.is_system && (
                      <span className="text-[8px] font-black text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-md uppercase tracking-wide">
                        {t("padrão")}
                      </span>
                    )}
                    <button
                      onClick={() => startEdit(stage)}
                      className="p-1 hover:bg-purple-50 hover:text-purple-600 rounded-lg transition-colors text-gray-300"
                      title={t("Editar")}
                    >
                      <Pencil className="w-3 h-3" />
                    </button>
                    <button
                      onClick={() => handleDelete(stage)}
                      disabled={stage.is_system}
                      className="p-1 hover:bg-red-50 hover:text-red-500 rounded-lg transition-colors text-gray-300 disabled:opacity-30 disabled:cursor-not-allowed"
                      title={
                        stage.is_system
                          ? t("Etapa padrão — não pode ser excluída")
                          : t("Excluir")
                      }
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </>
                )}
              </div>
            ))
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100 shrink-0">
          <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest text-center">
            {pipelineId
              ? t(
                  "Etapas do funil do Virtus Chat · Etapa com negócios só pode ser excluída depois de movê-los",
                )
              : t(
                  'Etapas padrão podem ser renomeadas mas não excluídas · Leads em etapas excluídas vão para "Novo"',
                )}
          </p>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
// Tag Manager Modal (mantido para uso em CampaignLeads)
// ---------------------------------------------------------------------------

export function TagManagerModal({
  open,
  onClose,
  onTagsChange,
}: {
  open: boolean;
  onClose: () => void;
  onTagsChange: (tags: LeadTag[]) => void;
}) {
  const { t } = useTranslation();
  const { activeContext } = useActiveContext();
  const [tags, setTags] = useState<LeadTag[]>([]);
  const [newName, setNewName] = useState("");
  const [newColor, setNewColor] = useState("#8b5cf6");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (activeContext?.organization?.id)
        params.append("organization_id", String(activeContext.organization.id));
      if (activeContext?.clientCompany?.id)
        params.append("company_id", String(activeContext.clientCompany.id));
      const data = await apiService.get(`/api/ads/leads/tags?${params}`);
      if (data.success) {
        setTags(data.tags || []);
        onTagsChange(data.tags || []);
      }
    } catch {
      /* ignore */
    }
  }, [activeContext, onTagsChange]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const data = await apiService.post("/api/ads/leads/tags", {
        name: newName.trim(),
        color: newColor,
      });
      if (data.success) {
        setNewName("");
        showToastSuccess(t("Tag criada!"));
        load();
      } else {
        showToastError(data.error || t("Erro ao criar tag"));
      }
    } catch (e: any) {
      showToastError(e.message || t("Erro ao criar tag"));
    } finally {
      setSaving(false);
    }
  };

  /** Apaga aqui, no LeadFlow e no Virtus Chat; antes, avisa onde a tag é usada. */
  const handleDelete = async (tagId: number) => {
    let uses: string[] = [];
    try {
      const data = await apiService.get(`/api/ads/leads/tags/${tagId}/usage`);
      const usage = data.usage || {};
      uses = [
        ...(usage.audiences || []),
        ...(usage.chat?.automations || []).map(
          (name: string) => `${t("Automação do Chat")}: ${name}`,
        ),
        ...(usage.chat?.broadcasts || []).map(
          (name: string) => `${t("Campanha do Chat")}: ${name}`,
        ),
      ];
    } catch {
      // Sem a consulta, confirma sem a lista.
    }
    const { isConfirmed } = await Swal.fire({
      title: t("Apagar tag?"),
      html: [
        t("A tag sai de todos os leads aqui, no LeadFlow e no Virtus Chat."),
        uses.length
          ? `<br/><br/><b>${t("Ela é usada em")}:</b><br/>${uses.map((u) => u.replace(/</g, "&lt;")).join("<br/>")}`
          : "",
      ].join(""),
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: t("Apagar"),
      cancelButtonText: t("Cancelar"),
      confirmButtonColor: "#EF4444",
    });
    if (!isConfirmed) return;
    try {
      await apiService.delete(`/api/ads/leads/tags/${tagId}`);
      showToastSuccess(t("Tag removida"));
      load();
    } catch (e: any) {
      showToastError(e.message || t("Erro ao remover tag"));
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("Gerenciar Tags")}
      subtitle={t("Crie tags para segmentar audiências")}
      icon={<Tag className="w-5 h-5" />}
      maxWidth="md"
    >
      <div className="flex flex-col p-6">
        <div className="flex flex-col gap-2 mb-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              placeholder={t("Nome da tag...")}
              maxLength={50}
              className="flex-1 min-w-0 h-10 px-3 text-[11px] font-bold rounded-xl border border-purple-200 focus:outline-hidden focus:ring-2 focus:ring-purple-300 bg-gray-50"
            />
            <button
              onClick={handleCreate}
              disabled={saving || !newName.trim()}
              className="w-10 h-10 bg-purple-600 text-white rounded-xl flex items-center justify-center hover:bg-purple-700 transition-colors disabled:opacity-50 shrink-0"
            >
              {saving ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Plus className="w-4 h-4" />
              )}
            </button>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {PRESET_COLORS.slice(0, 8).map((c) => (
              <button
                key={c}
                onClick={() => setNewColor(c)}
                className={`w-5 h-5 rounded-full transition-transform ${newColor === c ? "scale-125 ring-2 ring-offset-1 ring-gray-400" : ""}`}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        <div className="space-y-2 max-h-72 overflow-y-auto">
          {tags.length === 0 ? (
            <p className="text-center text-[10px] text-gray-400 py-8 font-bold uppercase tracking-widest">
              {t("Nenhuma tag criada ainda")}
            </p>
          ) : (
            tags.map((tag) => (
              <div
                key={tag.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-xl border border-gray-100 hover:border-purple-200 transition-colors"
              >
                <div
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: tag.color }}
                />
                <span className="flex-1 text-[11px] font-bold text-gray-700">
                  {tag.name}
                </span>
                {tag.meta_audience_id && (
                  <span className="text-[8px] font-black text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded-md uppercase tracking-wide">
                    Meta
                  </span>
                )}
                <button
                  onClick={() => handleDelete(tag.id)}
                  className="p-1 hover:bg-red-50 hover:text-red-500 rounded-lg transition-colors text-gray-300"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))
          )}
        </div>

        <div className="mt-4 pt-4 border-t border-gray-100">
          <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest text-center">
            {t(
              "Tags com Meta Audience sincronizam automaticamente ao marcar o lead",
            )}
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default LeadKanban;
